import React from 'react';
import { Control, Controller, FieldArrayWithId } from 'react-hook-form';
import { Autocomplete, Input, Select } from '@lemon-io/ui-kit';

import { ProjectQuestion } from '@internal/types/projects';

import type { SchemaType } from './apply-form';
import {
  AgreeCheckbox,
  AgreeError,
  Card,
  FieldAnchor,
  FieldsColumn,
  FieldsRow,
  FormCardTitle,
  INFO_BANNER_ICON_COLOR,
  InfoBanner,
  InfoBannerIcon,
  InfoBannerText,
  RateFieldsColumn,
} from './project-view.styled';

export const sortProjectQuestions = (
  questions: ProjectQuestion[]
): ProjectQuestion[] =>
  [...questions].sort((a, b) => {
    if (typeof a.order === 'number' && typeof b.order === 'number')
      return a.order - b.order;
    if (typeof a.order === 'number') return -1;
    if (typeof b.order === 'number') return 1;
    return 0;
  });

const RATE_COSTS_HINT =
  'For remote roles, consider your overall costs (such as taxes, time off, and other expenses) when setting your preferred range.';

const OVERLAP_HINT =
  'Check the client’s time zone and try to ensure the maximum overlap possible.';

const RATE_COMMENT_PLACEHOLDER =
  'You can add optional monthly/yearly pay expectations in USD before taxes, or other rate details.';

const blockNonNumericKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (/^[-+e,]$/u.test(event.key)) event.preventDefault();
};

interface Option {
  label: string;
  value: string;
}

interface ApplyFormFieldsProps {
  control: Control<SchemaType>;
  fields: Array<FieldArrayWithId<SchemaType, 'questions', 'id'>>;
  questionTitles: string[];
  timeOptions: Option[];
  timeZoneOptions: Option[];
  showRateExpectations?: boolean;
  showAgreement?: boolean;
  agreeDisabled?: boolean;
  disabled?: boolean;
  overlapRequirement?: string | null;
}

const ApplyFormFields: React.FC<ApplyFormFieldsProps> = ({
  control,
  fields,
  questionTitles,
  timeOptions,
  timeZoneOptions,
  showRateExpectations = false,
  showAgreement = false,
  agreeDisabled = false,
  disabled = false,
  overlapRequirement,
}) => (
  <>
    <Card>
      <FormCardTitle>Basic details</FormCardTitle>
      <FieldsColumn>
        <FieldAnchor data-field="basicDetails.preferredStartDate">
          <Controller
            control={control}
            name="basicDetails.preferredStartDate"
            render={({ field, fieldState }) => (
              <Input
                label="Preferred start day *"
                placeholder="E.g. anytime, or in 2 weeks"
                variant="standard"
                size="small"
                multiline
                extraOptions={{ minRows: 1 }}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={disabled}
                dataTestId="apply-start-date"
              />
            )}
          />
        </FieldAnchor>
        <FieldAnchor data-field="basicDetails.vacationPlans">
          <Controller
            control={control}
            name="basicDetails.vacationPlans"
            render={({ field, fieldState }) => (
              <Input
                label="Vacation plans *"
                placeholder="Dates, or no planned vacation"
                variant="standard"
                size="small"
                multiline
                extraOptions={{ minRows: 1 }}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={disabled}
                dataTestId="apply-vacation-plans"
              />
            )}
          />
        </FieldAnchor>
        <FieldAnchor data-field="basicDetails.timeslots">
          <Controller
            control={control}
            name="basicDetails.timeslots"
            render={({ field, fieldState }) => (
              <Input
                label="Time-slots for interview with a client *"
                placeholder="E.g. Fri 4-8PM, or a Calendly link"
                variant="standard"
                size="small"
                multiline
                extraOptions={{ minRows: 1 }}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={disabled}
                dataTestId="apply-timeslots"
              />
            )}
          />
        </FieldAnchor>
      </FieldsColumn>
    </Card>

    {showRateExpectations && (
      <Card data-testid="apply-rate-expectations">
        <FormCardTitle>Desired rate (USD per hour)</FormCardTitle>
        <RateFieldsColumn>
          <FieldsRow>
            <FieldAnchor data-field="basicDetails.minRate">
              <Controller
                control={control}
                name="basicDetails.minRate"
                render={({ field, fieldState }) => (
                  <Input
                    placeholder="From"
                    type="number"
                    variant="standard"
                    size="small"
                    extraOptions={{
                      InputProps: { endAdornment: null },
                      inputProps: { min: 1 },
                    }}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    onKeyDown={blockNonNumericKeys}
                    error={fieldState.error?.message}
                    disabled={disabled}
                    dataTestId="apply-min-rate"
                  />
                )}
              />
            </FieldAnchor>
            <FieldAnchor data-field="basicDetails.maxRate">
              <Controller
                control={control}
                name="basicDetails.maxRate"
                render={({ field, fieldState }) => (
                  <Input
                    placeholder="To"
                    type="number"
                    variant="standard"
                    size="small"
                    extraOptions={{
                      InputProps: { endAdornment: null },
                      inputProps: { min: 1 },
                    }}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    onKeyDown={blockNonNumericKeys}
                    error={fieldState.error?.message}
                    disabled={disabled}
                    dataTestId="apply-max-rate"
                  />
                )}
              />
            </FieldAnchor>
          </FieldsRow>
          <FieldAnchor data-field="basicDetails.rateComment">
            <Controller
              control={control}
              name="basicDetails.rateComment"
              render={({ field, fieldState }) => (
                <Input
                  label="Rate comment"
                  placeholder={RATE_COMMENT_PLACEHOLDER}
                  variant="standard"
                  size="small"
                  multiline
                  extraOptions={{ minRows: 1 }}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  disabled={disabled}
                  dataTestId="apply-rate-comment"
                />
              )}
            />
          </FieldAnchor>
          {!disabled && (
            <InfoBanner>
              <InfoBannerIcon size={18} color={INFO_BANNER_ICON_COLOR} />
              <InfoBannerText>{RATE_COSTS_HINT}</InfoBannerText>
            </InfoBanner>
          )}
        </RateFieldsColumn>
      </Card>
    )}

    <Card>
      <FormCardTitle>Working hours</FormCardTitle>
      {!disabled && (
        <InfoBanner $belowTitle>
          <InfoBannerIcon size={18} color={INFO_BANNER_ICON_COLOR} />
          <InfoBannerText>
            {OVERLAP_HINT}
            {overlapRequirement &&
              ` Project requirement: ${overlapRequirement.replace(
                /\.$/u,
                ''
              )}.`}
          </InfoBannerText>
        </InfoBanner>
      )}
      <FieldsColumn>
        <FieldsRow>
          <FieldAnchor data-field="workingHours.startTime">
            <Controller
              control={control}
              name="workingHours.startTime"
              render={({ field, fieldState }) => (
                <Select
                  label="Start working hours *"
                  variant="standard"
                  size="small"
                  id="apply-start-hours"
                  options={timeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? null}
                  disabled={disabled}
                  dataTestId="apply-start-hours"
                />
              )}
            />
          </FieldAnchor>
          <FieldAnchor data-field="workingHours.endTime">
            <Controller
              control={control}
              name="workingHours.endTime"
              render={({ field, fieldState }) => (
                <Select
                  label="End working hours *"
                  variant="standard"
                  size="small"
                  id="apply-end-hours"
                  options={timeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? null}
                  disabled={disabled}
                  dataTestId="apply-end-hours"
                />
              )}
            />
          </FieldAnchor>
        </FieldsRow>
        <FieldAnchor data-field="workingHours.timeZone">
          <Controller
            control={control}
            name="workingHours.timeZone"
            render={({ field, fieldState }) => (
              <Autocomplete
                label="Time zone *"
                variant="standard"
                size="small"
                options={timeZoneOptions}
                filterSelectedOptions={false}
                highlightSearch
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={disabled}
                dataTestId="apply-timezone"
              />
            )}
          />
        </FieldAnchor>
      </FieldsColumn>
    </Card>

    <Card>
      <FormCardTitle>Application questions</FormCardTitle>
      <FieldsColumn>
        {fields.map((field, index) => (
          <FieldAnchor key={field.id} data-field={`questions.${index}.answer`}>
            <Controller
              control={control}
              name={`questions.${index}.answer`}
              render={({ field: formField, fieldState }) => (
                <Input
                  label={`${questionTitles[index]} *`}
                  variant="standard"
                  size="small"
                  multiline
                  extraOptions={{ minRows: 1 }}
                  helperText={`${(formField.value ?? '').length}/1000`}
                  value={formField.value}
                  onChange={formField.onChange}
                  onBlur={formField.onBlur}
                  error={fieldState.error?.message}
                  disabled={disabled}
                  dataTestId={`apply-question-${index}`}
                />
              )}
            />
          </FieldAnchor>
        ))}
        <FieldAnchor data-field="additionalComments">
          <Controller
            control={control}
            name="additionalComments"
            render={({ field, fieldState }) => (
              <Input
                label="Any additional comments or highlights? Feel free to add here (optional)"
                variant="standard"
                size="small"
                multiline
                extraOptions={{ minRows: 1 }}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={disabled}
                dataTestId="apply-comments"
              />
            )}
          />
        </FieldAnchor>
        {showAgreement && (
          <FieldAnchor data-field="agree">
            <Controller
              control={control}
              name="agree"
              render={({ field, fieldState }) => {
                const showError = Boolean(fieldState.error) && !field.value;
                return (
                  <>
                    <AgreeCheckbox
                      $error={showError}
                      color="secondary"
                      checked={Boolean(field.value)}
                      onChange={field.onChange}
                      label="By checking this box, you confirm you won’t combine this project with any other full-time job outside Lemon.io. If your situation changes, you must notify the Lemon.io team. Failure to comply may result in removal from the project and loss of future opportunities."
                      disabled={agreeDisabled}
                    />
                    {showError && (
                      <AgreeError data-testid="apply-agree-error">
                        {fieldState.error?.message}
                      </AgreeError>
                    )}
                  </>
                );
              }}
            />
          </FieldAnchor>
        )}
      </FieldsColumn>
    </Card>
  </>
);

export default ApplyFormFields;
